/**
 * Home Page
 * =========
 * Landing page with hero section and key content blocks.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Play, TrendingUp, Users, Target, Zap } from 'lucide-react';
import { Button } from '../../components/shared/Button';
import { FeatureCard, StatCard } from '../../components/shared/Card';
import { SectionHeader } from '../../components/shared/SectionBadge';
import { FadeReveal, StaggerReveal } from '../../components/animations/FadeReveal';
import { FloatingElement } from '../../components/animations/ParallaxLayer';
import { FEATURES, RESULTS_STATS } from '../../utils/constants';
import { Icon } from '../../utils/iconMap.jsx';

/**
 * Home Page Component
 * Main landing page for the DME-CPH website.
 * 
 * @returns {JSX.Element} Home page
 */
function Home() {
  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-screen bg-dme-blue-800 flex items-center">
        {/* Background Pattern */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Bright blue gradient from bottom-right at 45 degrees */}
          <div 
            className="absolute bottom-0 right-0 w-[800px] h-[800px] opacity-40 rounded-full blur-3xl"
            style={{
              background: 'radial-gradient(circle at bottom right, var(--dme-accent-orange) 0%, transparent 70%)',
              transform: 'translate(30%, 30%)',
            }}
          />
          {/* Additional glow layer */}
          <div 
            className="absolute bottom-0 right-0 w-[600px] h-[600px] opacity-30 rounded-full blur-2xl"
            style={{
              background: 'radial-gradient(circle at bottom right, var(--dme-accent-orange) 0%, transparent 60%)',
              transform: 'translate(20%, 20%)',
            }}
          />
          
          {/* Floating Shapes */}
          <FloatingElement amplitude={20} duration={15} className="absolute top-1/4 right-1/4">
            <div className="w-16 h-16 border-2 border-white/20 rounded-lg rotate-12" />
          </FloatingElement>
          <FloatingElement amplitude={25} duration={18} delay={2} className="absolute bottom-1/3 left-1/4">
            <div className="w-12 h-12 border-2 border-dme-orange/30 rounded-full" />
          </FloatingElement>
          <FloatingElement amplitude={15} duration={20} delay={4} className="absolute top-1/3 left-1/3">
            <div className="w-8 h-8 bg-white/10 rounded-lg -rotate-12" />
          </FloatingElement>
        </div>
        
        {/* Hero Content */}
        <div className="container-dme relative z-10 py-32 lg:py-40">
          <div className="max-w-4xl">
            <FadeReveal direction="up" distance={40} duration={0.8}>
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white/90 text-sm font-medium mb-6">
                <Zap className="w-4 h-4 text-dme-orange" />
                Now with AI-Powered Predictions
              </span>
            </FadeReveal>
            
            <FadeReveal direction="up" distance={40} duration={0.8} delay={0.1}>
              <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl xl:text-7xl text-white leading-tight mb-6">
                Data-Driven
                <span className="text-dme-orange"> Enrollment </span>
                Marketing
              </h1>
            </FadeReveal>
            
            <FadeReveal direction="up" distance={40} duration={0.8} delay={0.2}>
              <p className="text-lg lg:text-xl text-white/80 max-w-2xl mb-8 leading-relaxed">
                Transform your enrollment strategy with predictive analytics and 
                AI-powered insights. Make data-driven decisions that boost recruitment 
                and retention.
              </p>
            </FadeReveal>
            
            <FadeReveal direction="up" distance={40} duration={0.8} delay={0.3}>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/features">
                  <Button 
                    variant="primary" 
                    size="lg"
                    rightIcon={<ArrowRight className="w-5 h-5" />}
                  >
                    Explore Features
                  </Button>
                </Link>
                <Link to="/demo">
                  <Button 
                    variant="outline-white" 
                    size="lg"
                    rightIcon={<Play className="w-5 h-5" />}
                  >
                    Watch Demo
                  </Button>
                </Link>
              </div>
            </FadeReveal>
          </div>
        </div>
        
        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
            <div className="w-1.5 h-3 bg-white/50 rounded-full animate-bounce" />
          </div>
        </div>
      </section>
      
      {/* Features Section */}
      <section className="section bg-white">
        <div className="container-dme">
          <FadeReveal>
            <SectionHeader
              badge="Features"
              title="Everything You Need"
              subtitle="Powerful tools and features designed specifically for higher education enrollment marketing."
              align="center"
              className="mb-16"
            />
          </FadeReveal>
          
          <StaggerReveal 
            selector=".feature-card"
            stagger={0.1}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8"
          >
            {FEATURES.slice(0, 6).map((feature) => (
              <div key={feature.id} className="feature-card">
                <FeatureCard
                  icon={<Icon name={feature.icon} size={28} />}
                  title={feature.title}
                  description={feature.description}
                  color={feature.color}
                />
              </div>
            ))}
          </StaggerReveal>
        </div>
      </section>
      
      {/* Stats Section */}
      <section className="section bg-dme-blue-50">
        <div className="container-dme">
          <FadeReveal>
            <SectionHeader
              badge="Results"
              title="Proven Impact"
              subtitle="See the measurable results our partners have achieved with DME-CPH."
              align="center"
              className="mb-16"
            />
          </FadeReveal>
          
          <StaggerReveal 
            selector=".stat-card"
            stagger={0.15}
            className="grid grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {RESULTS_STATS.map((stat, index) => (
              <div key={index} className="stat-card">
                <StatCard
                  value={stat.value}
                  suffix={stat.suffix}
                  label={stat.label}
                  description={stat.description}
                />
              </div>
            ))}
          </StaggerReveal>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="section bg-dme-blue-800">
        <div className="container-dme">
          <div className="max-w-3xl mx-auto text-center">
            <FadeReveal>
              <SectionHeader
                badge="Get Started"
                title="Ready to Transform Your Enrollment?"
                subtitle="Join hundreds of institutions using DME-CPH to drive better enrollment outcomes."
                align="center"
                light
                className="mb-10"
              />
            </FadeReveal>
            
            <FadeReveal delay={0.2}>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contact">
                  <Button 
                    variant="primary" 
                    size="lg"
                    rightIcon={<ArrowRight className="w-5 h-5" />}
                  >
                    Schedule a Demo
                  </Button>
                </Link>
                <Link to="/how-it-works">
                  <Button 
                    variant="outline-white" 
                    size="lg"
                  >
                    Learn More
                  </Button>
                </Link>
              </div>
            </FadeReveal>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Home;
